import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { MemberDetailPanel } from '@components/MemberDetailPanel';
import { LookupUnstop } from '@components/lookup/LookupUnstop';
import { useFeatures } from '@lib/clients';
import { lookupUnstopShown } from '@lib/quick-keys';
import {
  BrowseToolbar, SpacerRow, interleaveDetail, useBrowseVirtual,
} from '@components/lookup/browse-kit';
import { GridFrame, GridHeadRow, GridToolbar, gridCell } from '@components/grid/CareGrid';
import { useGridView, type GridColumn } from '@components/grid/useGridView';
import { multiSort } from '@lib/grid';
import { SlicerRail } from '@components/lookup/SlicerRail';
import { lookupAudit, memberDetail, memberList, memberListExport, memberResults, memberSearch } from '@lib/care-api';
import { hayIncludes } from '@lib/hay';
import {
  EMPTY_LOOKUP_FILTERS, activeFilterCount, buildMemberAxes, cohortLabel, hasResultSelection, matchMemberOps,
  splitResultFilters, toResultSelection, type LookupFilters,
} from '@lib/lookup-axes';
import {
  MILESTONE_LABEL, charmSortValue, dnsCharm, fmtCharmTs, milestoneCharm,
} from '@lib/lookup-charms';
import { findRosterMember } from '@lib/member-deep-link';
import { fmtMemberId } from '@lib/member-id';
import type {
  EmptyReason, MemberBrowseRow, MemberDetailResp, MemberListResp, MemberOutcomeRow,
  MemberResultFacets, MemberResultsResp,
} from '@lib/care-model';
import { EMPTY_REASON_META, emptyNotice, emptyRow, emptyTone } from '@lib/care-model';
import { isValidReason, useSessionReason } from '@lib/useSessionReason';

/**
 * MemberLookupSection - the Member tab as a browse-all roster table (2026-07-14 rework, William:
 * "make this lookup just like the one for the main portal"), brought to the Portal's as-built shape
 * on 19 Sep 2026 (row 418, Frontend's spec section 2 and MemberLookup.tsx MemberBrowseSection):
 *
 *   COLUMNS   a leading toggle column, then Member (id and name), Cohort, City / State, Status,
 *             Events, Last activity, the Portal's order and wording. Every header sorts, FIRST CLICK
 *             ASCENDING (the Portal's rule; Care's grid used to open Events and Last activity
 *             descending). The Portal's "Supp. File" column is OMITTED: it is derived on the Portal
 *             from the supplemental sent-log manifest in the Portal's own SharePoint tree
 *             (supplemental-sent-log.ts), which Care's roster read does not carry.
 *   SEARCH    one box, "Member ID, name, tracking #", client-side over member_id, full_name,
 *             outbound_tracking and return_tracking (the Portal's haystack), deferred so typing stays
 *             smooth on 21,000 rows; Care's phone path stays (a phone-shaped query resolves server-side).
 *             A settled non-empty search fires ONE audit beacon; a committed filter change fires one.
 *   STATUS    the Portal's pills where Care's data carries them: Do not solicit (standing in for the
 *             Portal's reply-classified "Do not contact"), and the milestone pill (Resulted /
 *             Delivered to lab) ONLY for a member the operator's Result selection asked about. The
 *             Portal's Status pill says "Resulted", a milestone and not the outcome; Care shows the
 *             same word. The Portal's opted-out, opt-in and "Request a kit" pills need its reply
 *             corpus and prohibitor store, which Care's roster row does not carry.
 *   EXPORT    "Export to Excel" exports exactly the rows on screen (search and slicers applied, in the
 *             current sort); the page re-prompts for an EXPORT-scoped reason first and the export runs
 *             with that reason, the session grant untouched.
 *   FOCUS     a member arriving by the `?member=` link or a PCP-panel jump is opened inline and scrolled
 *             into the viewport under the sticky header; a link to a member not on the roster is
 *             reported to the page, which says so.
 *
 * ONE THING IS NOT CLIENT-SIDE, AND DELIBERATELY SO (QA-113 option 4, William 2026-09-06).
 *
 * The roster that loads here carries NO screening outcome. The Result chips in the rail come from
 * server-computed FACET COUNTS, which say how many members hold each result and name none of them.
 * When the operator actually selects a Result chip she is asking about those members, so the section
 * calls /api/care/member-results, which audits the ask and returns an OVERLAY - member id plus
 * outcome - for exactly the members that match. The overlay is merged onto the rows on screen. His
 * 19 Sep word restores a RESULTS TAB beside this one; the browse row here stays option 4.
 */

type ColKey = 'member' | 'cohort' | 'location' | 'status' | 'events' | 'last';

/** The Member tab's columns in the Portal's order and wording (MEMBER_BROWSE_COLS, minus Supp. File). */
export const MEMBER_COLUMNS: ReadonlyArray<{ key: ColKey; label: string }> = [
  { key: 'member', label: 'Member' },
  { key: 'cohort', label: 'Cohort' },
  { key: 'location', label: 'City / State' },
  { key: 'status', label: 'Status' },
  { key: 'events', label: 'Events' },
  { key: 'last', label: 'Last activity' },
];

/** The Portal's search haystack for the Member tab, in one place so a test can pin it. */
export function memberHay(r: MemberBrowseRow): string {
  return `${r.member_id} ${r.full_name} ${r.outbound_tracking} ${r.return_tracking}`.toLowerCase();
}

function sortValue(r: MemberBrowseRow, key: ColKey): string | number {
  switch (key) {
    case 'member': return (r.full_name || '').toLowerCase();
    case 'cohort': return (r.cohort || '').toLowerCase();
    case 'location': return `${r.city} ${r.state}`.toLowerCase();
    // While browsing, the only charm in this cell is the do-not-solicit mark, because no outcome
    // is on the row (QA-113 option 4). Sorting on the milestone timestamp alone would make the
    // column inert exactly when it is most used, so the mark leads and the timestamp breaks ties
    // among the members whose outcome the operator did ask for.
    case 'status': return `${dnsCharm(r) ? '0' : '1'}${charmSortValue(r)}`;
    case 'events': return r.event_count;
    case 'last': return r.last_activity || '';
  }
}

/** The Status cell's pills: the do-not-solicit mark always, and the milestone (Resulted /
 *  Delivered to lab, dated) only for a member the operator's Result selection asked about, because
 *  only those rows carry an outcome at all (QA-113 option 4). */
function StatusCharms({ r, outcomeShown }: { r: MemberBrowseRow; outcomeShown: boolean }) {
  const ms = milestoneCharm(r);
  const dns = dnsCharm(r);
  if (!ms && !dns) {
    return (
      <span
        className="muted"
        title={outcomeShown
          ? 'No status for this member.'
          : 'Result state is hidden until you filter by a Result chip. The counts in the rail show how many members hold each result.'}
      >
        -
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
      {dns && <span className="pill pill-danger" title={`do-not-solicit: ${r.do_not_solicit}`}>Do not solicit</span>}
      {ms && (
        <span className={ms.kind === 'resulted' ? 'pill pill-success' : 'pill pill-primary'}>
          {MILESTONE_LABEL[ms.kind]}
          {fmtCharmTs(ms.ts) && <span style={{ opacity: 0.75 }}>{fmtCharmTs(ms.ts)}</span>}
        </span>
      )}
    </span>
  );
}

export interface MemberLookupSectionProps {
  /** The page opens the reason modal. */
  onNeedReason: () => void;
  /** The export re-prompt: a continuation run once the export-scoped reason is given. */
  onRequireExportReason: (proceed: (reason: string) => void) => void;
  /** Fire-and-forget beacons: the settled non-empty search; the committed filter selection. */
  onAuditSearch?: (query: string) => void;
  onAuditFilter?: (filters: Record<string, string[]>) => void;
  /** A member's PCP on the record jumps to the PCP tab. */
  onJumpToPcp?: (key: string) => void;
  /** A member to open on arrival (the link, or a PCP-panel jump). */
  focusMember?: { id: string; via: 'link' | 'jump' } | null;
  /** Called once the focus was resolved: the roster id opened, or null when the member is not on it. */
  onFocusHandled?: (hit: string | null) => void;
}

export function MemberLookupSection(props: MemberLookupSectionProps) {
  const { onNeedReason, onRequireExportReason, onAuditSearch, onAuditFilter, onJumpToPcp, focusMember, onFocusHandled } = props;
  const sessionReason = useSessionReason();
  const reasonValid = isValidReason(sessionReason);

  const [rows, setRows] = useState<MemberBrowseRow[]>([]);
  const [facets, setFacets] = useState<MemberResultFacets | undefined>(undefined);
  const [total, setTotal] = useState(0);
  const [available, setAvailable] = useState(true);
  const [emptyReason, setEmptyReason] = useState<EmptyReason | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState('');
  const [loadedFor, setLoadedFor] = useState('');

  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [filters, setFilters] = useState<LookupFilters>(EMPTY_LOOKUP_FILTERS);

  const [openId, setOpenId] = useState<string | null>(null);
  const [pendingScroll, setPendingScroll] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, MemberDetailResp | 'loading' | 'error'>>({});
  // BB1 (22 Oct 2026): the Unstop on Care's own line shows on a card with a STOP while the server says so.
  const features = useFeatures();
  const [note, setNote] = useState('');

  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState('');

  // Load the whole roster once per valid session reason (the reason change re-audits the read).
  useEffect(() => {
    if (!reasonValid || loadedFor === sessionReason) return;
    let cancelled = false;
    setLoading(true);
    setLoadErr('');
    void memberList(sessionReason)
      .then((r) => {
        if (cancelled) return;
        if (r.ok) {
          const d = r.data as MemberListResp;
          const list = Array.isArray(d.rows) ? d.rows : [];
          setRows(list);
          setFacets(d.facets);
          setTotal(d.total ?? list.length);
          setAvailable(d.available !== false);
          setEmptyReason(d.emptyReason);
          setLoadedFor(sessionReason);
        } else {
          setLoadErr(`Roster load failed (HTTP ${r.status}).`);
        }
        // The load is over on BOTH branches, set HERE rather than in a finally: a finally runs a
        // microtask after the state above, by which time React may have re-run this effect (loadedFor
        // changed) and flipped `cancelled`, leaving `loading` stuck true (found by the sections test).
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadErr('Roster load failed.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reasonValid, sessionReason, loadedFor]);

  // The search beacon (the Portal's N6): once per SETTLED, non-empty term, never per keystroke.
  useEffect(() => {
    const q = deferredSearch.trim();
    if (q) onAuditSearch?.(q);
  }, [deferredSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // The filter beacon: on the COMMITTED selection, skipping the empty initial one.
  const filtersSeen = useRef(false);
  useEffect(() => {
    if (!filtersSeen.current) {
      filtersSeen.current = true;
      if (activeFilterCount(filters) === 0) return;
    }
    onAuditFilter?.(filters);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── The Result axes, answered by the server (QA-113 option 4) ───────────────────────────────
  const resultSel = useMemo(() => toResultSelection(filters), [filters]);
  const resultActive = useMemo(() => hasResultSelection(filters), [filters]);
  const resultKey = useMemo(
    () => JSON.stringify({
      v: [...resultSel.resultValue].sort(),
      s: [...resultSel.resultStatus].sort(),
      m: [...resultSel.milestone].sort(),
    }),
    [resultSel],
  );
  const [outcomes, setOutcomes] = useState<{ key: string; byId: Map<string, MemberOutcomeRow> } | null>(null);
  const [outcomeLoading, setOutcomeLoading] = useState(false);
  const [outcomeErr, setOutcomeErr] = useState('');
  const [outcomeEmpty, setOutcomeEmpty] = useState<EmptyReason | undefined>(undefined);

  useEffect(() => {
    if (!reasonValid || !resultActive) {
      setOutcomes(null);
      setOutcomeErr('');
      setOutcomeEmpty(undefined);
      setOutcomeLoading(false);
      return;
    }
    let cancelled = false;
    setOutcomeLoading(true);
    setOutcomeErr('');
    // The selection is rebuilt FROM THE KEY, not read from `resultSel`, so an unrelated chip click
    // never refires this effect (and never writes a fresh audited disclosure row).
    const k = JSON.parse(resultKey) as { v: string[]; s: string[]; m: Array<'resulted' | 'delivered_to_lab'> };
    void memberResults(sessionReason, { resultValue: k.v, resultStatus: k.s, milestone: k.m })
      .then((r) => {
        if (cancelled) return;
        if (r.ok) {
          const d = r.data as MemberResultsResp;
          const list = Array.isArray(d.rows) ? d.rows : [];
          setOutcomes({ key: resultKey, byId: new Map(list.map((o) => [o.member_id, o])) });
          setOutcomeEmpty(d.emptyReason);
        } else {
          // NO SILENT FALL-BACK: the rows stay hidden and the operator is told.
          setOutcomes(null);
          setOutcomeErr(`Result filter could not be applied (HTTP ${r.status}).`);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setOutcomes(null);
        setOutcomeErr('Result filter could not be applied.');
      })
      .finally(() => {
        if (!cancelled) setOutcomeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reasonValid, sessionReason, resultActive, resultKey]);

  // Rail filters + search + sort, in memory for every axis whose data is on a row (the Portal browse
  // model). The Result axes are applied by the overlay instead.
  const railFiltered = useMemo(() => {
    const { local } = splitResultFilters(filters);
    const base = Object.keys(local).length ? rows.filter((r) => matchMemberOps(r, local)) : rows;
    if (!resultActive) return base;
    if (!outcomes || outcomes.key !== resultKey) return [];
    const byId = outcomes.byId;
    const out: MemberBrowseRow[] = [];
    for (const r of base) {
      const o = byId.get(r.member_id);
      if (o) out.push({ ...r, ...o });
    }
    return out;
  }, [rows, filters, resultActive, outcomes, resultKey]);

  // Phone lookup (Alignment feedback 2026-08): the browse rows carry NO phone columns, so a
  // phone-shaped query is resolved SERVER-side through the same last-10 phone index the drawer's
  // typeahead uses, and the returned member ids light up their rows here. Fail-soft.
  const phoneQ = useMemo(() => {
    const t = deferredSearch.trim();
    const digits = t.replace(/\D+/g, '');
    return /^[\d\s()+.-]+$/.test(t) && digits.length >= 10 && digits.length <= 11 ? t : '';
  }, [deferredSearch]);
  const [phoneHits, setPhoneHits] = useState<{ q: string; ids: ReadonlySet<string> } | null>(null);
  useEffect(() => {
    if (!phoneQ) {
      setPhoneHits(null);
      return;
    }
    let cancelled = false;
    void memberSearch(phoneQ, 50)
      .then((r) => {
        if (cancelled) return;
        setPhoneHits({ q: phoneQ, ids: new Set((r.candidates ?? []).map((c) => c.member_id)) });
      })
      .catch(() => {
        if (!cancelled) setPhoneHits(null);
      });
    return () => {
      cancelled = true;
    };
  }, [phoneQ]);

  const filtered = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    if (!q) return railFiltered;
    const phoneIds = phoneQ && phoneHits && phoneHits.q === phoneQ ? phoneHits.ids : null;
    return railFiltered.filter((r) => {
      if (phoneIds?.has(r.member_id)) return true;
      return hayIncludes(memberHay(r), q);
    });
  }, [railFiltered, deferredSearch, phoneQ, phoneHits]);

  const axes = useMemo(() => buildMemberAxes(rows, facets), [rows, facets]);

  const toggleRow = async (memberId: string) => {
    if (openId === memberId) {
      setOpenId(null);
      return;
    }
    setOpenId(memberId);
    if (details[memberId] && details[memberId] !== 'error') return;
    setDetails((prev) => ({ ...prev, [memberId]: 'loading' }));
    try {
      const r = await memberDetail(memberId, sessionReason);
      if (r.ok && (r.data as MemberDetailResp).found) {
        setDetails((prev) => ({ ...prev, [memberId]: r.data as MemberDetailResp }));
      } else {
        setDetails((prev) => ({ ...prev, [memberId]: 'error' }));
        setNote(`Full record unavailable for #${fmtMemberId(memberId)} (HTTP ${r.status}).`);
      }
    } catch {
      setDetails((prev) => ({ ...prev, [memberId]: 'error' }));
      setNote(`Full record unavailable for #${fmtMemberId(memberId)}.`);
    }
  };

  // The drilled-in / linked member: resolved once the roster is on hand, opened, scrolled to, and
  // The toggle column lives inside a memoized `columns` array (row 356's grid habit), whose deps are
  // the open row and the loaded details, NOT the session reason. A closure captured there before the
  // grant would call the reveal with an EMPTY reason and be refused by the server (found by the shell
  // test on 19 Sep 2026: memberDetail called with ''). The column therefore calls through a ref that
  // always holds the current toggleRow.
  const toggleRef = useRef(toggleRow);
  toggleRef.current = toggleRow;
  // (by link) audited as a search that arrived by link. Not on the roster: reported to the page.
  useEffect(() => {
    if (!focusMember || !reasonValid || loading) return;
    if (rows.length === 0 && !loadErr) return; // nothing loaded yet
    const hit = findRosterMember(rows, focusMember.id);
    if (hit) {
      setSearch('');
      setFilters(EMPTY_LOOKUP_FILTERS);
      setOpenId(hit);
      setPendingScroll(hit);
      const cur = details[hit];
      if (cur === undefined || cur === 'error') void toggleRow(hit).then(() => setOpenId(hit));
      if (focusMember.via === 'link') lookupAudit('member', 'search', { memberId: hit, via: 'link' });
    }
    onFocusHandled?.(hit);
  }, [focusMember, reasonValid, loading, rows, loadErr]); // eslint-disable-line react-hooks/exhaustive-deps

  /** ROW 356's shared table habits on the roster, with the Portal's column set (row 418). */
  const columns = useMemo<GridColumn<MemberBrowseRow>[]>(() => [
    {
      key: 'toggle', label: '', menuLabel: 'Expand', locked: true, stopRowClick: true, defaultWidth: 36,
      thStyle: { width: 36 }, tdStyle: { textAlign: 'center' },
      render: (r) => {
        const open = openId === r.member_id;
        return (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ padding: '0 6px' }}
            aria-expanded={open}
            aria-label={open ? 'Hide member detail' : 'Show full member detail'}
            title={open ? 'Hide member detail' : 'Show full member detail'}
            onClick={() => void toggleRef.current(r.member_id)}
          >
            {details[r.member_id] === 'loading' ? '...' : open ? '▾' : '▸'}
          </button>
        );
      },
    },
    {
      key: 'member', label: 'Member', tdStyle: { whiteSpace: 'nowrap' }, sortValue: (r) => sortValue(r, 'member'),
      render: (r) => (
        <>
          <b>{r.full_name || 'member'}</b>
          <span className="muted small" style={{ marginLeft: 6, fontFamily: 'var(--font-mono)' }}>
            {fmtMemberId(r.member_id)}
          </span>
        </>
      ),
    },
    { key: 'cohort', label: 'Cohort', tdClassName: 'small', sortValue: (r) => sortValue(r, 'cohort'), render: (r) => (r.cohort ? cohortLabel(r.cohort) : <span className="muted">-</span>) },
    {
      key: 'location', label: 'City / State', tdClassName: 'small', tdStyle: { whiteSpace: 'nowrap' }, sortValue: (r) => sortValue(r, 'location'),
      render: (r) => [r.city, r.state].filter(Boolean).join(', ') || <span className="muted">-</span>,
    },
    { key: 'status', label: 'Status', sortValue: (r) => sortValue(r, 'status'), render: (r) => <StatusCharms r={r} outcomeShown={resultActive} /> },
    // First click ascending on EVERY header (the Portal's rule): no firstDir here.
    { key: 'events', label: 'Events', align: 'right', tdClassName: 'small num-cell', sortValue: (r) => sortValue(r, 'events'), render: (r) => r.event_count },
    {
      key: 'last', label: 'Last activity', tdClassName: 'small', tdStyle: { whiteSpace: 'nowrap' }, sortValue: (r) => sortValue(r, 'last'),
      render: (r) => r.last_activity || <span className="muted">-</span>,
    },
  ], [details, openId, resultActive]); // eslint-disable-line react-hooks/exhaustive-deps
  const view = useGridView('lookup-members', columns, { defaultSort: [{ k: 'member', asc: true }] });
  const byKey = useMemo(() => new Map(columns.map((c) => [c.key, c])), [columns]);
  const sorted = useMemo(
    () => multiSort(filtered, view.sort, (r, k) => byKey.get(k)?.sortValue?.(r)),
    [filtered, view.sort, byKey],
  );
  const span = Math.max(1, view.visible.length);

  const displayItems = useMemo(
    () => interleaveDetail(sorted, (r) => r.member_id === openId),
    [sorted, openId],
  );
  const virtual = useBrowseVirtual(displayItems, (it) => `${it.kind}:${it.row.member_id}`, 500);

  // Bring the drilled-in row into view once it exists in displayItems (under the sticky header).
  useEffect(() => {
    if (!pendingScroll) return;
    const idx = displayItems.findIndex((it) => it.kind === 'row' && it.row.member_id === pendingScroll);
    if (idx < 0) {
      if (!loading && rows.length > 0) setPendingScroll(null);
      return;
    }
    setPendingScroll(null);
    virtual.scrollToIndex(idx);
  }, [pendingScroll, displayItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const runExport = async (exportReason: string) => {
    setExporting(true);
    setExportErr('');
    try {
      const { blob, filename } = await memberListExport(exportReason, sorted);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportErr(e instanceof Error ? e.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  if (!reasonValid) {
    return (
      <div className="note" style={{ marginBottom: 14 }}>
        Provide a reason-for-access to load the member roster.{' '}
        <button type="button" className="btn btn-sm" onClick={onNeedReason}>Provide reason</button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
      <BrowseToolbar
        search={search}
        onSearch={setSearch}
        placeholder="Member ID, name, tracking #"
        ariaLabel="Search the member roster (member ID, name, tracking number, or a phone number)"
        shown={sorted.length}
        total={rows.length}
        noun="members"
        right={
          <button
            type="button"
            className="btn btn-sm"
            disabled={exporting || sorted.length === 0}
            title="Export the filtered members (current view) to Excel"
            onClick={() => {
              setExportErr('');
              // The export needs its OWN export-scoped reason, even with the session grant active.
              onRequireExportReason((exportReason) => { void runExport(exportReason); });
            }}
          >
            {exporting ? 'Exporting...' : 'Export to Excel'}
          </button>
        }
      />
      {exportErr && <div className="note danger" style={{ marginBottom: 14 }}>{exportErr}</div>}

      {note && (
        <div className="note" style={{ marginBottom: 14 }}>
          {note}
        </div>
      )}
      {loadErr && (
        <div className="note danger" style={{ marginBottom: 14 }}>
          {loadErr}
        </div>
      )}
      {outcomeErr && (
        <div className="note danger" style={{ marginBottom: 14 }}>
          {outcomeErr} No members are shown, because the Result filter you set could not be applied
          and showing the unfiltered roster would ignore it. Clear the Result chips to browse again.
        </div>
      )}
      {resultActive && outcomeLoading && !outcomeErr && (
        <div className="muted small" style={{ marginBottom: 8 }}>Applying the Result filter...</div>
      )}
      {resultActive && !outcomeLoading && !outcomeErr && outcomeEmpty && (
        <div className={`note ${emptyTone(outcomeEmpty)}`} style={{ marginBottom: 14 }}>
          {emptyNotice(outcomeEmpty, 'No member matches that Result selection.')}
        </div>
      )}
      {!resultActive && facets?.available && (
        <div className="muted small" style={{ marginBottom: 8 }}>
          Result state is hidden while you are browsing. The Result chips in the rail carry the
          counts; select one to see which members hold it (that ask is audited), or open the Results tab.
        </div>
      )}
      {!resultActive && facets && !facets.available && (
        <div className="note" style={{ marginBottom: 14 }}>
          Result filters are unavailable for this campaign, so the rail shows no Result chips. That
          is not the same as no member having a result.
        </div>
      )}
      {!available && !loading && !loadErr && (
        <div className={`note ${emptyTone(emptyReason)}`} style={{ marginBottom: 14 }}>
          {emptyNotice(emptyReason, 'Member roster unavailable in this campaign DB.')}
        </div>
      )}
      {available && !loading && !loadErr && emptyReason && EMPTY_REASON_META[emptyReason].notice && (
        <div className={`note ${emptyTone(emptyReason)}`} style={{ marginBottom: 14 }}>
          {EMPTY_REASON_META[emptyReason].notice}
        </div>
      )}
      {rows.length < total && (
        <div className="muted small" style={{ marginBottom: 8 }}>
          Showing the first {rows.length.toLocaleString()} of {total.toLocaleString()} members (server cap).
        </div>
      )}

      <GridFrame view={view}>
      <GridToolbar view={view} />
      <div ref={virtual.scrollRef} className="card card-flush" style={{ maxHeight: view.expanded ? 'calc(100vh - 90px)' : '65vh', overflow: 'auto' }}>
        <table className="dt">
          <thead>
            <GridHeadRow view={view} />
          </thead>
          <tbody>
            {displayItems.length === 0 && (
              <tr>
                <td colSpan={span} className="muted" style={{ textAlign: 'center', padding: '20px 0' }}>
                  {loading ? 'Loading members...' : emptyRow(emptyReason, search.trim() ? 'No members match this search.' : 'No members found for this campaign.')}
                </td>
              </tr>
            )}
            <SpacerRow height={virtual.padTop} colSpan={span} />
            {virtual.items.map((vi) => {
              const item = displayItems[vi.index];
              if (!item) return null;
              const r = item.row;
              if (item.kind === 'detail') {
                const d = details[r.member_id];
                return (
                  <tr key={vi.key} data-index={vi.index} ref={virtual.measureRef}>
                    <td colSpan={span} style={{ background: 'var(--paper-2)', padding: '12px 16px', height: 'auto' }}>
                      {d === 'loading' || d === undefined ? (
                        <span className="muted small">Loading member detail...</span>
                      ) : d === 'error' ? (
                        <span className="muted small">Could not load this member.</span>
                      ) : (
                        <>
                          {/* BB1 (22 Oct 2026): the Unstop from the account, on a card that shows the STOP (switch on only). */}
                          {lookupUnstopShown(features.unstopCareLine, d.optOut?.class) && <LookupUnstop memberId={r.member_id} />}
                          <MemberDetailPanel detail={d} onJumpToPcp={onJumpToPcp} />
                        </>
                      )}
                    </td>
                  </tr>
                );
              }
              const expanded = openId === r.member_id;
              return (
                <tr
                  key={vi.key}
                  data-index={vi.index}
                  ref={virtual.measureRef}
                  tabIndex={0}
                  aria-expanded={expanded}
                  aria-label={`${r.full_name || 'member'} ${fmtMemberId(r.member_id)}`}
                  onClick={() => void toggleRow(r.member_id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      void toggleRow(r.member_id);
                    }
                  }}
                  style={{ cursor: 'pointer', background: expanded ? 'var(--paper-2)' : undefined }}
                >
                  {view.visible.map((c) => (
                    <td key={c.key} {...gridCell(view, c, r)}>
                      {c.render(r, vi.index)}
                    </td>
                  ))}
                </tr>
              );
            })}
            <SpacerRow height={virtual.padBottom} colSpan={span} />
          </tbody>
        </table>
      </div>
      </GridFrame>
      </div>
      <SlicerRail
        axes={axes}
        filters={filters}
        onFilters={setFilters}
        scope={{ shown: railFiltered.length, total: rows.length }}
        noun="members"
      />
    </div>
  );
}
